import ReglasAgrupadas from "./ReglasAgrupadas";
// src/pages/automatizacion/AutomatizarEfectores.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import efectorService from "../../services/efectorService";
import automationEfectoresService from "../../services/automationEfectoresService";
import Tooltip from "../Tooltip";
import { usePermisos } from "../../context/PermisosContext";
import { isSuperAdmin } from "../../services/authUtils";

const TIPOS = [
  { value: 1, label: "Cíclica" },
  { value: 2, label: "Ventana" },
  { value: 3, label: "Puntual" },
];

const ACCIONES = [
  { value: true, label: "Encender" },
  { value: false, label: "Apagar" },
];

const normalizarHoraEscrita = (valor) => {
  const digitos = String(valor || "").replace(/\D/g, "").slice(0, 4);
  return digitos.length > 2 ? `${digitos.slice(0, 2)}:${digitos.slice(2)}` : digitos;
};

// bit0=Dom … bit6=Sab (igual al modelo ReglaEfector)
const DOW = [
  { bit: 1 << 0, label: "Dom" },
  { bit: 1 << 1, label: "Lun" },
  { bit: 1 << 2, label: "Mar" },
  { bit: 1 << 3, label: "Mié" },
  { bit: 1 << 4, label: "Jue" },
  { bit: 1 << 5, label: "Vie" },
  { bit: 1 << 6, label: "Sáb" },
];

const initNueva = {
  estanqueId: "",
  dispositivoId: "",
  efectorId: "",
  tipo: 1,
  encender: true,
  estaActivo: true,
  intervaloMin: "",
  duracionOnMin: "",
  horaInicioHHMM: "",
  duracionMin: "",
  diasMask: null,
};

export default function AutomatizarEfectores() {
  const { can, canEdit } = usePermisos();
  const esSuper = useMemo(() => !!isSuperAdmin(), []);
  const puedeVer = esSuper || !!can?.("AUTOMATIZACION_VIEW");
  const puedeEditar = esSuper || !!canEdit?.("AUTOMATIZACION_EDIT");

  // Estructura Estanque -> Dispositivos -> Efectores (del backend existente)
  const [estructura, setEstructura] = useState([]);
  const [estanqueId, setEstanqueId] = useState("");
  const [dispositivoId, setDispositivoId] = useState("");
  const [efectorId, setEfectorId] = useState("");

  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busquedaReglas, setBusquedaReglas] = useState("");
  const [verArchivadas, setVerArchivadas] = useState(false);
  const reglasFiltradas = useMemo(() => {
    const q = busquedaReglas.trim().toLocaleLowerCase("es");
    if (!q) return reglas;
    return reglas.filter((r) => JSON.stringify(r).toLocaleLowerCase("es").includes(q));
  }, [reglas, busquedaReglas]);

  // edición inline
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ ...initNueva });

  // --------- helpers tiempo/días ----------
  const minutesFromHHMM = (v) => {
    if (!v) return null;
    const parts = String(v).split(":");
    if (parts.length !== 2) return null;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    const tot = h * 60 + m;
    return tot >= 0 && tot <= 1439 ? tot : null;
  };
  const hhmmFromMinutes = (m) => {
    if (m == null) return "";
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  };
  const toggleDia = (mask, bit) => {
    const cur = mask ?? 0;
    return (cur & bit) === 0 ? cur | bit : cur & ~bit;
  };
  const diasToText = (mask) => {
    if (mask == null) return "Todos";
    if (mask === 0) return "—";
    return DOW.filter((d) => (mask & d.bit) !== 0)
      .map((d) => d.label)
      .join(", ");
  };

  // --------- carga estructura ----------
  const cargarEstructura = useCallback(async () => {
    if (!puedeVer) return;
    try {
      const data = await efectorService.obtenerTodosLosEfectores();
      setEstructura(data || []);
    } catch (e) {
      console.error(e);
      alert("No se pudo cargar la estructura de efectores.");
    }
  }, [puedeVer]);

  useEffect(() => {
    cargarEstructura();
  }, [cargarEstructura]);

  const estanques = useMemo(
    () =>
      (estructura || []).map((e) => ({
        id: e.estanqueId ?? e.EstanqueId,
        nombre: e.estanqueNombre ?? e.EstanqueNombre,
        dispositivos: (e.dispositivos ?? e.Dispositivos ?? []).map((d) => ({
          id: d.dispositivoId ?? d.DispositivoId,
          nombre: d.dispositivoNombre ?? d.DispositivoNombre,
          efectores: (d.efectores ?? d.Efectores ?? []).map((f) => ({
            id: f.id ?? f.Id,
            nombre: f.nombre ?? f.Nombre,
          })),
        })),
      })),
    [estructura]
  );

  const dispositivosFiltrados = useMemo(() => {
    const est = estanques.find((x) => String(x.id) === String(estanqueId));
    return est ? est.dispositivos : [];
  }, [estanques, estanqueId]);

  const efectoresFiltrados = useMemo(() => {
    const devs = dispositivosFiltrados;
    if (!dispositivoId) {
      return devs.flatMap((d) => d.efectores);
    }
    const d = devs.find((x) => String(x.id) === String(dispositivoId));
    return d ? d.efectores : [];
  }, [dispositivosFiltrados, dispositivoId]);

  // reset combos dependientes
  useEffect(() => {
    setDispositivoId("");
    setEfectorId("");
    setEditId(null);
  }, [estanqueId]);
  useEffect(() => {
    setEfectorId("");
    setEditId(null);
  }, [dispositivoId]);

  // --------- cargar reglas filtradas ----------
  const cargarReglas = useCallback(
    async (opt) => {
      if (!puedeVer) return;
      const filters = {
        estanqueId: opt?.estanqueId ?? (estanqueId || undefined),
        dispositivoId: opt?.dispositivoId ?? (dispositivoId || undefined),
        efectorId: opt?.efectorId ?? (efectorId || undefined),
        archivadas: opt?.archivadas ?? verArchivadas,
      };
      setLoading(true);
      try {
        const data = await automationEfectoresService.listarReglas(filters);
        setReglas(data || []);
      } catch (e) {
        console.error(e);
        alert("No se pudieron cargar las reglas.");
      } finally {
        setLoading(false);
      }
    },
    [puedeVer, estanqueId, dispositivoId, efectorId, verArchivadas]
  );

  useEffect(() => {
    cargarReglas();
  }, [estanqueId, dispositivoId, efectorId, cargarReglas]);

  // --------- crear ----------
  const [nueva, setNueva] = useState({ ...initNueva });
  useEffect(() => {
    setNueva((f) => ({
      ...f,
      estanqueId,
      dispositivoId,
      efectorId,
    }));
  }, [estanqueId, dispositivoId, efectorId]);

  const onNuevaChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNueva((f) => ({
      ...f,
      [name]:
        type === "checkbox"
          ? checked
          : name === "encender"
          ? value === "true"
          : name === "horaInicioHHMM"
          ? normalizarHoraEscrita(value)
          : value,
    }));
  };
  const onNuevaToggleDia = (bit) =>
    setNueva((f) => ({ ...f, diasMask: toggleDia(f.diasMask, bit) }));

  const submitNueva = async (e) => {
    e.preventDefault();
    if (!puedeEditar) return;
    if (!nueva.efectorId) {
      alert("Selecciona un efector.");
      return;
    }
    if ((Number(nueva.tipo) === 2 || Number(nueva.tipo) === 3) && minutesFromHHMM(nueva.horaInicioHHMM) == null) {
      alert("Escribe una hora válida en formato HH:MM, por ejemplo 07:30.");
      return;
    }
    const payload = {
      efectorId: Number(nueva.efectorId),
      estaActivo: !!nueva.estaActivo,
      tipo: Number(nueva.tipo),
      encender: !!nueva.encender,
      intervaloMin:
        Number(nueva.tipo) === 1 && String(nueva.intervaloMin) !== ""
          ? Number(nueva.intervaloMin)
          : null,
      duracionOnMin:
        Number(nueva.tipo) === 1 && String(nueva.duracionOnMin) !== ""
          ? Number(nueva.duracionOnMin)
          : null,
      horaInicioMin:
        (Number(nueva.tipo) === 2 || Number(nueva.tipo) === 3) && nueva.horaInicioHHMM
          ? minutesFromHHMM(nueva.horaInicioHHMM)
          : null,
      duracionMin:
        Number(nueva.tipo) === 2 && String(nueva.duracionMin) !== ""
          ? Number(nueva.duracionMin)
          : null,
      dias: nueva.diasMask,
    };

    setSaving(true);
    try {
      await automationEfectoresService.crearRegla(payload);
      await cargarReglas();
      setNueva((_) => ({
        ...initNueva,
        estanqueId,
        dispositivoId,
        efectorId,
        tipo: 1,
        encender: true,
        estaActivo: true,
      }));
    } catch (err) {
      console.error(err);
      alert(err?.response?.data ?? "No se pudo crear la regla.");
    } finally {
      setSaving(false);
    }
  };

  // --------- editar ----------
  const startEdit = (r) => {
    const id = r.id ?? r.Id;
    setEditId(id);
    setEdit({
      ...initNueva,
      estanqueId,
      dispositivoId,
      efectorId: r.efectorId ?? r.EfectorId,
      tipo: Number(r.tipo ?? r.Tipo),
      encender:
        (typeof r.encender === "boolean" ? r.encender : r.Encender) ?? true,
      estaActivo: r.estaActivo ?? r.EstaActivo,
      intervaloMin: r.intervaloMin ?? r.IntervaloMin ?? "",
      duracionOnMin: r.duracionOnMin ?? r.DuracionOnMin ?? "",
      horaInicioHHMM: hhmmFromMinutes(r.horaInicioMin ?? r.HoraInicioMin ?? null),
      duracionMin: r.duracionMin ?? r.DuracionMin ?? "",
      diasMask: r.dias ?? r.Dias ?? null,
    });
  };
  const cancelEdit = () => setEditId(null);
  const onEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEdit((f) => ({
      ...f,
      [name]:
        type === "checkbox"
          ? checked
          : name === "encender"
          ? value === "true"
          : name === "horaInicioHHMM"
          ? normalizarHoraEscrita(value)
          : value,
    }));
  };
  const onEditToggleDia = (bit) =>
    setEdit((f) => ({ ...f, diasMask: toggleDia(f.diasMask, bit) }));

  const saveEdit = async () => {
    if (!puedeEditar || !editId) return;
    if ((Number(edit.tipo) === 2 || Number(edit.tipo) === 3) && minutesFromHHMM(edit.horaInicioHHMM) == null) {
      alert("Escribe una hora válida en formato HH:MM, por ejemplo 07:30.");
      return;
    }

    const payload = {
      efectorId: Number(edit.efectorId),
      estaActivo: !!edit.estaActivo,
      tipo: Number(edit.tipo),
      encender: !!edit.encender,
      intervaloMin:
        Number(edit.tipo) === 1 && String(edit.intervaloMin) !== ""
          ? Number(edit.intervaloMin)
          : null,
      duracionOnMin:
        Number(edit.tipo) === 1 && String(edit.duracionOnMin) !== ""
          ? Number(edit.duracionOnMin)
          : null,
      horaInicioMin:
        (Number(edit.tipo) === 2 || Number(edit.tipo) === 3) && edit.horaInicioHHMM
          ? minutesFromHHMM(edit.horaInicioHHMM)
          : null,
      duracionMin:
        Number(edit.tipo) === 2 && String(edit.duracionMin) !== ""
          ? Number(edit.duracionMin)
          : null,
      dias: edit.diasMask,
    };

    try {
      await automationEfectoresService.actualizarRegla(editId, payload);
      await cargarReglas();
      setEditId(null);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data ?? "No se pudo actualizar la regla.");
    }
  };

  // --------- acciones fila ----------
  const onToggle = async (id) => {
    if (!puedeEditar) return;
    try {
      await automationEfectoresService.toggleRegla(id);
      await cargarReglas();
    } catch (e) {
      console.error(e);
      alert("No se pudo cambiar el estado.");
    }
  };
  const onArchivar = async (id) => {
    if (!puedeEditar) return;
    if (!window.confirm("¿Archivar la regla? Dejará de ejecutarse y podrás recuperarla después.")) return;
    try {
      await automationEfectoresService.archivarRegla(id);
      await cargarReglas();
    } catch (e) {
      console.error(e);
      alert("No se pudo archivar la regla.");
    }
  };

  const onRecuperar = async (id) => {
    if (!puedeEditar) return;
    try { await automationEfectoresService.recuperarRegla(id); await cargarReglas(); }
    catch (e) { console.error(e); alert(e?.response?.data ?? "No se pudo recuperar la regla."); }
  };

  if (!puedeVer) {
    return (
      <div className="p-6 bg-neutral-950 text-neutral-300 rounded-lg max-w-4xl mx-auto">
        No tienes permisos para ver Automatización.
      </div>
    );
  }

  // --------- UI ----------
  const TT_CICLO =
    "Cíclica: cada N minutos se enciende por M minutos. Ej: N=15, M=2 → ON 2 min cada 15 min.";
  const TT_VENTANA =
    "Ventana: a la hora HH:MM se enciende y dura X minutos. Se puede limitar por días.";
  const TT_PUNTUAL =
    "Puntual: dispara exactamente a HH:MM en los días seleccionados. Es una repetición semanal, no una fecha única.";

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-emerald-400">
            Automatización de Efectores
          </h1>
          <p className="text-sm text-gray-300">
            Crea reglas de tiempo para encender/apagar efectores sin depender de sensores.
          </p>
        </div>
        {esSuper && (
          <span className="text-xs border border-emerald-500/60 px-2 py-1 rounded text-emerald-300">
            Superadministrador
          </span>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-[#111319] rounded-2xl p-4 border border-[#232635] mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-sm text-gray-300">Estanque</label>
            <select
              className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
              value={estanqueId}
              onChange={(e) => setEstanqueId(e.target.value)}
            >
              <option value="">Seleccione…</option>
              {estanques.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-300">Dispositivo</label>
            <select
              className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
              value={dispositivoId}
              onChange={(e) => setDispositivoId(e.target.value)}
              disabled={!estanqueId}
            >
              <option value="">
                {estanqueId ? "Todos del estanque…" : "Elige estanque primero"}
              </option>
              {dispositivosFiltrados.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nombre} (#{d.id})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-300">Efector</label>
            <select
              className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
              value={efectorId}
              onChange={(e) => setEfectorId(e.target.value)}
              disabled={!estanqueId}
            >
              <option value="">
                {estanqueId ? "Todos del dispositivo…" : "Elige estanque primero"}
              </option>
              {efectoresFiltrados.map((f) => (
                <option key={f.id} value={f.id}>
                  #{f.id} — {f.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Crear nueva */}
      <form
        onSubmit={submitNueva}
        className="bg-[#111319] rounded-2xl p-4 border border-[#232635] mb-4"
      >
        <h3 className="text-emerald-400 font-semibold mb-3">Crear nueva regla</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-sm text-gray-300">Efector</label>
            <select
              name="efectorId"
              value={nueva.efectorId}
              onChange={onNuevaChange}
              disabled={!estanqueId || !puedeEditar}
              className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
              required
            >
              <option value="">
                {estanqueId ? "Seleccione…" : "Elige un estanque primero"}
              </option>
              {efectoresFiltrados.map((f) => (
                <option key={f.id} value={f.id}>
                  #{f.id} — {f.nombre}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-300">Tipo</label>
            <select
              name="tipo"
              value={nueva.tipo}
              onChange={onNuevaChange}
              disabled={!puedeEditar}
              className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
            >
              {TIPOS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Acción */}
          <div>
            <label className="text-sm text-gray-300">Acción</label>
            <select
              name="encender"
              value={String(nueva.encender)}
              onChange={onNuevaChange}
              disabled={!puedeEditar}
              className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
            >
              {ACCIONES.map((a) => (
                <option key={String(a.value)} value={String(a.value)}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <label className={`inline-flex items-center gap-2 text-sm ${!puedeEditar ? "opacity-60" : ""}`}>
              <input
                type="checkbox"
                name="estaActivo"
                checked={!!nueva.estaActivo}
                onChange={onNuevaChange}
                disabled={!puedeEditar}
              />
              Activa
            </label>
          </div>

          {/* Cíclica */}
          {Number(nueva.tipo) === 1 && (
            <>
              <Tooltip text={TT_CICLO}>
                <div>
                  <label className="text-sm text-gray-300 cursor-help">
                    Intervalo (min) ℹ️
                  </label>
                  <input
                    name="intervaloMin"
                    value={nueva.intervaloMin}
                    onChange={onNuevaChange}
                    type="number"
                    min="1"
                    step="1"
                    disabled={!puedeEditar}
                    className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
                    placeholder="Ej. 15"
                  />
                </div>
              </Tooltip>
              <div>
                <label className="text-sm text-gray-300">Duración ON (min)</label>
                <input
                  name="duracionOnMin"
                  value={nueva.duracionOnMin}
                  onChange={onNuevaChange}
                  type="number"
                  min="0"
                  step="1"
                  disabled={!puedeEditar}
                  className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
                  placeholder="Ej. 2"
                />
              </div>
            </>
          )}

          {/* Ventana / Puntual */}
          {(Number(nueva.tipo) === 2 || Number(nueva.tipo) === 3) && (
            <>
              <Tooltip text={Number(nueva.tipo) === 2 ? TT_VENTANA : TT_PUNTUAL}>
                <div>
                  <label className="text-sm text-gray-300 cursor-help">
                    {Number(nueva.tipo) === 3 ? "Hora" : "Inicio (HH:MM)"} ℹ️
                  </label>
                    <input
                    name="horaInicioHHMM"
                    value={nueva.horaInicioHHMM}
                    onChange={onNuevaChange}
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="HH:MM"
                    aria-label="Hora en formato horas y minutos"
                    disabled={!puedeEditar}
                    className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
                  />
                  <p className="mt-1 text-xs text-gray-400">Formato de 24 horas (00:00–23:59): 12:36 es mediodía; 00:36 es 12:36 a. m. y 23:36 es 11:36 p. m.</p>
                </div>
              </Tooltip>

              {Number(nueva.tipo) === 2 && (
                <div>
                  <label className="text-sm text-gray-300">Duración (min)</label>
                  <input
                    name="duracionMin"
                    value={nueva.duracionMin}
                    onChange={onNuevaChange}
                    type="number"
                    min="1"
                    step="1"
                    disabled={!puedeEditar}
                    className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
                    placeholder="Ej. 60"
                  />
                </div>
              )}

              <div>
                <label className="text-sm text-gray-300">Días</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {DOW.map((d) => (
                    <button
                      type="button"
                      key={d.bit}
                      onClick={() => onNuevaToggleDia(d.bit)}
                      disabled={!puedeEditar}
                      className={`px-3 py-1 rounded-lg text-xs border ${
                        (nueva.diasMask ?? 0b1111111) & d.bit
                          ? "bg-emerald-600 border-emerald-700 text-white"
                          : "bg-[#0C0F16] border-[#2C2F40] text-gray-200"
                      } ${!puedeEditar ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Selección vacía = Todos los días.
                </div>
                <p className="mt-1 text-xs text-gray-400">Se repite cada semana en los días seleccionados. Las reglas actuales no usan una fecha de calendario.</p>
              </div>
            </>
          )}
        </div>

        <div className="mt-4 flex gap-3">
          <button
            type="submit"
            disabled={!estanqueId || saving || !puedeEditar}
            className={`px-5 py-2 rounded-xl font-semibold text-white shadow ${
              !estanqueId || saving || !puedeEditar
                ? "bg-gray-600 cursor-not-allowed"
                : "bg-violet-600 hover:bg-violet-700"
            }`}
          >
            {saving ? "Guardando…" : "Crear regla"}
          </button>
          <button
            type="button"
            onClick={() =>
              setNueva({
                ...initNueva,
                estanqueId,
                dispositivoId,
                efectorId,
              })
            }
            className="px-5 py-2 rounded-xl border border-[#2C2F40] text-gray-200 hover:bg-[#151826]"
          >
            Limpiar
          </button>
        </div>
      </form>

      {/* Listado */}
      <div className="bg-[#111319] rounded-2xl p-4 border border-[#232635] mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-emerald-400 font-semibold">Reglas {efectorId ? "del efector seleccionado" : dispositivoId ? "del dispositivo" : estanqueId ? "del estanque" : "de todos mis efectores"}</h3>
          <div className="flex items-center gap-3">{loading && <span className="text-xs text-gray-400">cargando…</span>}<button type="button" className="px-3 py-2 rounded-lg border border-[#2C2F40] text-sm" onClick={() => setVerArchivadas(v => !v)}>{verArchivadas ? "Ver vigentes" : "Ver archivadas"}</button></div>
        </div>

        <input value={busquedaReglas} onChange={(e) => setBusquedaReglas(e.target.value)}
          placeholder="Buscar reglas por nombre, tipo, acción, horario o estado…"
          className="w-full mb-3 bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2" />

        {reglasFiltradas.length === 0 ? (
          <div className="text-gray-400 text-sm">{loading ? "Cargando reglas…" : verArchivadas ? "No hay reglas archivadas que coincidan." : "No hay reglas que coincidan con los filtros."}</div>
        ) : (
          <ReglasAgrupadas reglas={reglasFiltradas} tipo="efectores">
            {(r) => {
              const id = r.id ?? r.Id;
              const enEdicion = editId === id;

              const tipo = Number(r.tipo ?? r.Tipo);
              const intervaloMin = r.intervaloMin ?? r.IntervaloMin ?? null;
              const duracionOnMin = r.duracionOnMin ?? r.DuracionOnMin ?? null;
              const horaInicioMin = r.horaInicioMin ?? r.HoraInicioMin ?? null;
              const duracionMin = r.duracionMin ?? r.DuracionMin ?? null;
              const dias = r.dias ?? r.Dias ?? null;
              const estaActivo = r.estaActivo ?? r.EstaActivo;
              const archivada = !!(r.archivadoUtc ?? r.ArchivadoUtc);
              const encender =
                (typeof r.encender === "boolean" ? r.encender : r.Encender) === false
                  ? false
                  : true;

              if (enEdicion) {
                return (
                  <div
                    key={id}
                    className="rounded-xl border border-[#2C2F40] bg-[#0C0F16] p-3"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="text-sm text-gray-300">Tipo</label>
                        <select
                          name="tipo"
                          value={edit.tipo}
                          onChange={onEditChange}
                          disabled={!puedeEditar}
                          className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
                        >
                          {TIPOS.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="text-sm text-gray-300">Acción</label>
                        <select
                          name="encender"
                          value={String(edit.encender)}
                          onChange={onEditChange}
                          disabled={!puedeEditar}
                          className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
                        >
                          {ACCIONES.map((a) => (
                            <option key={String(a.value)} value={String(a.value)}>
                              {a.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-end">
                        <label className={`inline-flex items-center gap-2 text-sm ${!puedeEditar ? "opacity-60" : ""}`}>
                          <input
                            type="checkbox"
                            name="estaActivo"
                            checked={!!edit.estaActivo}
                            onChange={onEditChange}
                            disabled={!puedeEditar}
                          />
                          Activa
                        </label>
                      </div>

                      {Number(edit.tipo) === 1 && (
                        <>
                          <div>
                            <label className="text-sm text-gray-300">Intervalo (min)</label>
                            <input
                              name="intervaloMin"
                              value={edit.intervaloMin}
                              onChange={onEditChange}
                              type="number"
                              min="1"
                              step="1"
                              disabled={!puedeEditar}
                              className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
                            />
                            <p className="mt-1 text-xs text-gray-400">Formato de 24 horas (00:00–23:59): 12:36 es mediodía; 00:36 es 12:36 a. m. y 23:36 es 11:36 p. m.</p>
                          </div>
                          <div>
                            <label className="text-sm text-gray-300">Duración ON (min)</label>
                            <input
                              name="duracionOnMin"
                              value={edit.duracionOnMin}
                              onChange={onEditChange}
                              type="number"
                              min="0"
                              step="1"
                              disabled={!puedeEditar}
                              className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
                            />
                          </div>
                        </>
                      )}

                      {(Number(edit.tipo) === 2 || Number(edit.tipo) === 3) && (
                        <>
                          <div>
                            <label className="text-sm text-gray-300">
                              {Number(edit.tipo) === 3 ? "Hora" : "Inicio (HH:MM)"}
                            </label>
                            <input
                              name="horaInicioHHMM"
                              value={edit.horaInicioHHMM}
                              onChange={onEditChange}
                              type="text"
                              inputMode="numeric"
                              maxLength={5}
                              placeholder="HH:MM"
                              aria-label="Hora en formato horas y minutos"
                              disabled={!puedeEditar}
                              className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
                            />
                          </div>

                          {Number(edit.tipo) === 2 && (
                            <div>
                              <label className="text-sm text-gray-300">Duración (min)</label>
                              <input
                                name="duracionMin"
                                value={edit.duracionMin}
                                onChange={onEditChange}
                                type="number"
                                min="1"
                                step="1"
                                disabled={!puedeEditar}
                                className="mt-1 w-full bg-[#0C0F16] text-gray-100 border border-[#2C2F40] rounded-xl px-3 py-2"
                              />
                            </div>
                          )}

                          <div>
                            <label className="text-sm text-gray-300">Días</label>
                            <div className="mt-1 flex flex-wrap gap-2">
                              {DOW.map((d) => (
                                <button
                                  type="button"
                                  key={d.bit}
                                  onClick={() => onEditToggleDia(d.bit)}
                                  disabled={!puedeEditar}
                                  className={`px-3 py-1 rounded-lg text-xs border ${
                                    (edit.diasMask ?? 0b1111111) & d.bit
                                      ? "bg-emerald-600 border-emerald-700 text-white"
                                      : "bg-[#0C0F16] border-[#2C2F40] text-gray-200"
                                  } ${!puedeEditar ? "opacity-60 cursor-not-allowed" : ""}`}
                                >
                                  {d.label}
                                </button>
                              ))}
                            </div>
                            <p className="mt-1 text-xs text-gray-400">Se repite cada semana en los días seleccionados. Las reglas actuales no usan una fecha de calendario.</p>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={saveEdit}
                        disabled={!puedeEditar}
                        className={`px-4 py-2 rounded-lg font-semibold ${
                          !puedeEditar ? "bg-gray-600 text-gray-200 cursor-not-allowed" : "bg-violet-600 hover:bg-violet-700 text-white"
                        }`}
                      >
                        Guardar
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 rounded-lg border border-[#2C2F40] text-gray-200 hover:bg-[#151826]"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                );
              }

              // vista de lectura
              const tipoTxt = TIPOS.find((x) => x.value === tipo)?.label;
              const accionTxt = encender ? "Encender" : "Apagar";

              let detalle = "";
              if (tipo === 1) {
                detalle = `Cada ${intervaloMin} min → ${accionTxt} ${duracionOnMin ?? 0} min`;
              } else if (tipo === 2) {
                detalle = `Inicio ${hhmmFromMinutes(horaInicioMin)} · ${accionTxt} · Duración ${duracionMin} min · Días: ${diasToText(dias)}`;
              } else {
                detalle = `${accionTxt} a las ${hhmmFromMinutes(horaInicioMin)} · Días: ${diasToText(dias)}`;
              }

              return (
                <div
                  key={id}
                  className="rounded-xl border border-[#2C2F40] bg-[#0C0F16] p-3 flex items-center justify-between"
                >
                  <div className="text-gray-200 text-sm">
                    <div className="font-semibold">
                      {r.efectorNombre || `Efector #${r.efectorId ?? r.EfectorId}`} · {tipoTxt}
                    </div>
                    <div className="text-gray-400">{detalle}</div>
                  </div>
                  <div className="flex gap-2">
                    {archivada ? <button onClick={() => onRecuperar(id)} disabled={!puedeEditar} className="px-3 py-1 rounded-lg text-xs font-semibold bg-emerald-700 text-white disabled:opacity-60">Recuperar</button> : <><button
                      onClick={() => startEdit(r)}
                      className="px-3 py-1 rounded-lg text-xs font-semibold bg-sky-600 hover:bg-sky-700 text-white"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => onToggle(id)}
                      disabled={!puedeEditar}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold ${
                        estaActivo
                          ? puedeEditar
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                            : "bg-gray-600 text-white cursor-not-allowed"
                          : puedeEditar
                            ? "bg-gray-600 hover:bg-gray-700 text-white"
                            : "bg-gray-600 text-white cursor-not-allowed"
                      }`}
                    >
                      {estaActivo ? "Activo" : "Inactivo"}
                    </button>
                    <button
                      onClick={() => onArchivar(id)}
                      disabled={!puedeEditar}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold ${puedeEditar ? "bg-amber-700 hover:bg-amber-800 text-white" : "bg-gray-600 text-white cursor-not-allowed"}`}
                    >
                      Archivar
                    </button></>}
                  </div>
                </div>
              );
            }}
          </ReglasAgrupadas>
        )}
      </div>

    </div>
  );
}
